// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import api models.
import {
    Address,
} from '@app/models/api';
// Import props.
import {
	InputAddressesSelectionListProps,
	InputFormProps,
} from '@app/props';

interface UseInputAddressesSelectionListParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	address1: Address;
	address1ButtonLabel: string;
	address2: Address;
	address2ButtonLabel: string;
	selectedAddress: Address | undefined;
	onSelectAddress?: (address: Address) => void;
	showValidation?: boolean;
}

type UseInputAddressesSelectionListResults = UseInputFormResult<Address | undefined, InputAddressesSelectionListProps>

const useInputAddressesSelectionList = (params: UseInputAddressesSelectionListParameters): UseInputAddressesSelectionListResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyAddressesSelectionListProps = (): InputAddressesSelectionListProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the addresses selection list props.
		return {
			...formProps,
			address1: params.address1,
			address1ButtonLabel: params.address1ButtonLabel,
			address2: params.address2,
			address2ButtonLabel: params.address2ButtonLabel,
			selectedAddress: other.value,
			onSelectAddress: selectAddress,
		}
	};

	// #endregion exported methods


	// #region private methods

	const selectAddress = (address: Address) => {
		other.setInput(address);
		// Invoke any call back delegation with the selected address.
		if (params.onSelectAddress !== undefined) {
			params.onSelectAddress(address);
		}
	}

	/**
	 * Validates the selected address.
	 * @param state
	 */
	const validateSelectedAddress = (address: Address | undefined, required: boolean): ValidatorResponse => {
		if (required
			&& address === undefined) {
			return new ValidatorResponse(false, 'Please select an address.');
		}

		return new ValidatorResponse(true, String.empty());
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'address selection list' input.
	const { applyProps, ...other } = useInputForm<Address | undefined>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: params.selectedAddress,
		showValidation: params.showValidation,
		validators: [validateSelectedAddress],
	});

	// #endregion variables


	return {
		applyProps: applyAddressesSelectionListProps,
		...other,
	};
}

// Export parameters.
export {
	UseInputAddressesSelectionListParameters,
}

// Export results.
export {
	UseInputAddressesSelectionListResults,
}

export default useInputAddressesSelectionList;